import React from 'react'
import PropTypes from 'prop-types'
import { LinkOutlined } from '@ant-design/icons'

// hixme-ui
import Container from '@hixme-ui/container'
import Text from '@hixme-ui/text'

import DetailsCardContainer from '../../../../components/DetailsCard/DetailsCardContainer'
import BenefitsSummaryButton from '../../components/BenefitsSummaryButton/BenefitsSummaryButton'
import {
  languageMapper,
  onBenefitsSummaryOpen,
} from '../../components/BundleCard/BundleCard'
import compareClasses from '../CompareTable/CompareTable.scss'
import classes from './DetailsTable.module.scss'

import { getBaseObject, getCategories, getBenefitList } from '../../utils/compareTable'
import { PersonBenefitPrice } from 'store/modules/persons/components'

const findPlanBenefit = (benefit, category, displayName) =>
  (benefit.bundleBenefits?.[category] || []).find(
    (item) => item.BenefitDisplayName === displayName
  )

const renderMobilePlan = (benefit, baseObject) => {
  const sbcLinks = benefit.SbcLinks || []

  return (
    <div key={benefit.BundlePublicKey} className={classes.mobilePlanBlock}>
      <div className={classes.mobilePlanHeader}>
        <Text large bolder>
          {benefit.MetalLevel?.toUpperCase()}
        </Text>
        <PersonBenefitPrice price={benefit.bundlePrice} />
      </div>

      <div className={classes.mobileSection}>
        <div className={classes.mobileSectionTitle}>Plan</div>
        <div className={classes.mobileRow}>
          <Text className={classes.mobileLabel}>Health plan</Text>
          <BenefitsSummaryButton
            label={benefit.PlanName}
            as="link"
            items={sbcLinks.map((link) => ({
              label: languageMapper[link.Language] || 'English',
              icon: <LinkOutlined />,
              key: link.Url,
            }))}
            onItemSelected={(key) => onBenefitsSummaryOpen(key, benefit.BundlePublicKey)}
          />
        </div>
        <div className={classes.mobileRow}>
          <Text className={classes.mobileLabel}>Style of network</Text>
          <Text thin>{benefit.PlanType}</Text>
        </div>
        <div className={classes.mobileRow}>
          <Text className={classes.mobileLabel}>Carrier</Text>
          <Text thin>{benefit.CarrierName}</Text>
        </div>
      </div>

      {Object.entries(baseObject).map(([category, items]) => (
        <div key={`${benefit.BundlePublicKey}-${category}`} className={classes.mobileSection}>
          <div className={classes.mobileSectionTitle}>{category}</div>
          {items.map((item) => {
            const planBenefit = findPlanBenefit(benefit, category, item.BenefitDisplayName) || {}

            return (
              <div key={item.BenefitDisplayName} className={classes.mobileRow}>
                <Text className={classes.mobileLabel}>{item.BenefitDisplayName}</Text>
                <Text thin>{planBenefit.DisplayValue || 'Not available'}</Text>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

const DetailsTable = ({ benefit, benefits, showFunctionality, groupFavoriteCount }) => {
  const plans = benefits?.length ? benefits : [benefit]
  const baseObject = getBaseObject(getCategories(plans))
  const benefitKeys = getBenefitList(baseObject)

  return (
    <>
      <div className={classes.mobileLayout}>
        {plans.map((plan) => renderMobilePlan(plan, baseObject))}
      </div>

      <div className={classes.root}>
        <div className={classes.desktopTable}>
        <div className={classes.keyColumn}>
          <Container flex noPadding width="100%" direction="column">
            <Container
              flex
              noPadding
              width="100%"
              height="170px"
              justifyContent="center"
              alignItems="center"
              direction="column"
            />
            <div className={compareClasses.titleKeyRow}>
              <Text uppercase>Plan</Text>
            </div>
            <div className={compareClasses.expandedKeyRow}>
              <Text>Health Plan</Text>
            </div>
            <div className={compareClasses.basicKeyRow}>
              <Text>Style of Network</Text>
            </div>
            <div className={compareClasses.basicKeyRow}>
              <Text>Carrier</Text>
            </div>
            {benefitKeys.map((item) => (
              <div key={item.key} title={item.title} className={compareClasses.basicKeyRow}>
                {item.text}
              </div>
            ))}
          </Container>
        </div>
        <div className={classes.planColumns}>
          {plans.map((plan) => (
            <div key={plan.BundlePublicKey} className={classes.planColumn}>
              <div className={classes.planCard}>
                <DetailsCardContainer
                  baseObject={baseObject}
                  benefit={plan}
                  groupFavoriteCount={groupFavoriteCount}
                  showFunctionality={showFunctionality}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </>
  )
}

DetailsTable.propTypes = {
  benefit: PropTypes.object.isRequired,
  benefits: PropTypes.arrayOf(PropTypes.object),
  groupFavoriteCount: PropTypes.number,
  showFunctionality: PropTypes.bool,
}

DetailsTable.defaultProps = {
  groupFavoriteCount: 0,
  showFunctionality: true,
}

export default DetailsTable
